import { EFinancingCompany } from '@if/core/types'
import {
  Checkbox,
  Divider,
  FormControlLabel,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { getMockUsers } from '@src/api/user-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface MockedLogin {
  id: string
  username: string
  roles: string[]
}

interface DialogProps {
  onMockedLoginSelected: (username: string, companies: EFinancingCompany[]) => void
  open: boolean
  onClose: () => void
}
interface MockedLoginItemProps {
  onTestCaseSelected: (username: string) => void
  mockedLogin: MockedLogin
  selected: boolean
  last: boolean
}

function loadMockedLogins(): Promise<MockedLogin[]> {
  return getMockUsers().then((response) => {
    const res = response.data as MockedLogin[]
    res.forEach((x: MockedLogin, index) => {
      x.id = index.toString()
    })
    return response.data as MockedLogin[]
  })
}

const TestCaseItem = ({ onTestCaseSelected, mockedLogin, selected, last }: MockedLoginItemProps) => {
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(mockedLogin.username)} selected={selected}>
        <ListItemText primary={mockedLogin.username} secondary={mockedLogin.roles.join(', ')} />
      </ListItemButton>
      {!last && <Divider />}
    </>
  )
}

const MockUserPicker = ({ open, onClose, onMockedLoginSelected }: DialogProps) => {
  const { t } = useTranslation()
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null)
  const [mockedLogins, setTestCases] = useState<MockedLogin[]>([])
  const [isLoading, setLoading] = useState(false)
  const [selectedCompanies, setSelectedCompanies] = useState<EFinancingCompany[]>([])

  useEffect(() => {
    if (open && mockedLogins.length === 0) {
      setLoading(true)
      setSelectedCompanies([EFinancingCompany.Iceberg])
      loadMockedLogins()
        .then((values: MockedLogin[]) => {
          setLoading(false)
          setTestCases(values)
        })
        .catch(reportErrorToConsole)
    }
  }, [open, mockedLogins])

  const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>, company: EFinancingCompany) => {
    if (event.target.checked) {
      setSelectedCompanies([...selectedCompanies, company])
    } else {
      setSelectedCompanies(selectedCompanies.filter((c) => c !== company))
    }
  }

  return (
    <FormDialog
      open={open}
      maxWidth="lg"
      dividers
      disableGutterContent
      title={
        <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="inherit">{t('login.mockLoginTitle')}</Typography>
          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompanies.includes(EFinancingCompany.IFinance)}
                  onChange={(event) => handleCompanyChange(event, EFinancingCompany.IFinance)}
                />
              }
              label="IFinance"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCompanies.includes(EFinancingCompany.Iceberg)}
                  onChange={(event) => handleCompanyChange(event, EFinancingCompany.Iceberg)}
                />
              }
              label="Iceberg"
            />
          </div>
        </Stack>
      }
      disabled={selectedUsername === null || selectedCompanies.length === 0}
      onClose={onClose}
      onConfirm={() => {
        if (selectedUsername) onMockedLoginSelected(selectedUsername, selectedCompanies)
      }}
    >
      {isLoading && <LinearProgress />}
      {!isLoading && (
        <List sx={{ padding: 0 }}>
          {mockedLogins.map((login, index) => (
            <TestCaseItem
              key={login.id}
              mockedLogin={login}
              onTestCaseSelected={setSelectedUsername}
              selected={login.username === selectedUsername}
              last={index === mockedLogins.length - 1}
            />
          ))}
        </List>
      )}
    </FormDialog>
  )
}

export default MockUserPicker
